import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { ArrowSquareOut } from '@phosphor-icons/react';

import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import { getResultDeliveryTag, renderStatusPill } from './constants';

// ENG-3107: cross-link from an inbound call to the outbound request whose result
// it delivered. The link is same-tab and same-route — Voice keeps its tab in the
// `type` query param — so `?type=Outbound&callId=<outbound_call_request_id>`
// switches to the Outbound table and opens that request's slide-out. `callId` is
// the outbound row identity (`id: request.id` in OutboundCalls::ListCallAttempts), so
// this deliberately uses the request id, not the attempt id.
//
// Deliberately carries ONLY the id. An earlier revision also passed the inbound
// `caller_name` as `search` to pull the target onto the outbound list's first page,
// which backfired twice over: OutboundCalls::ListCallAttempts matches the term against
// CONCAT_WS(' ', first, middle, last), so a "First Last" caller_name matches nothing
// once a middle name is stored — the user landed on an EMPTY table with a search
// chip they never typed, reading as "no outbound request exists for this patient".
// caller_name is also the name the voice agent heard, not the EHR name, and is
// frequently a proxy caller. It additionally put a patient name in the address bar
// (and, on a middle-click, in a real document request line) for no reliable benefit.
//
const outboundHref = (summary) => {
  const params = new URLSearchParams({ type: 'Outbound' });
  if (summary.outbound_call_request_id != null) {
    params.set('callId', String(summary.outbound_call_request_id));
  }
  return `?${params.toString()}`;
};

const ResultDeliveryPanel = ({ callLog, history }) => {
  const summary = callLog?.result_delivery;
  // Non-null tag implies a non-null summary — the tag is derived from
  // summary.state — so this single guard covers both.
  const tag = getResultDeliveryTag(callLog);
  if (!tag) return null;

  const href = outboundHref(summary);

  // Voice switches tabs through the router (see Voice/index.jsx), never through a
  // document navigation. Keeping a real href preserves middle-click, "open in new
  // tab" and keyboard semantics, but a plain left-click has to be handled in-app:
  // letting the browser follow it would full-reload the SPA, refetching the bundle
  // and tearing down this drawer. Modifier-clicks are left to the browser on
  // purpose. `history` is injected by withRouter and is not optional — there is no
  // fallback here on purpose, so a missing router fails loudly in development
  // instead of silently degrading to the full reload this exists to avoid.
  const handleClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    history.push(href);
  };

  return (
    <div className="inbound-result-delivery-panel">
      <Typography variant="text" size="small" bold color={COLORS.TEXT_PRIMARY_LITE}>
        Result Delivery
      </Typography>
      <div className="inbound-result-delivery-panel-body">
        {renderStatusPill(tag)}
        <Typography variant="text" size="xsmall" color={COLORS.TEXT_TERTIARY_LITE}>
          {tag.description}
          {/* Purely quantitative, so it adds a fact rather than restating the
              sentence above. The link below reaches one of these requests. */}
          {summary.delivery_count > 1 && ` This call covered ${summary.delivery_count} separate results.`}
        </Typography>
        {summary.outbound_call_request_id != null && (
          <a
            className="inbound-result-delivery-panel-link"
            href={href}
            onClick={handleClick}
            data-testid="result-delivery-outbound-link"
          >
            View in Voice Outbound
            <ArrowSquareOut size={14} weight="bold" aria-hidden="true" focusable="false" />
          </a>
        )}
      </div>
    </div>
  );
};

ResultDeliveryPanel.defaultProps = {
  callLog: null,
};

ResultDeliveryPanel.propTypes = {
  callLog: PropTypes.shape({
    result_delivery: PropTypes.shape({
      state: PropTypes.string,
      outbound_call_request_id: PropTypes.number,
      outbound_call_attempt_id: PropTypes.number,
      delivery_count: PropTypes.number,
    }),
  }),
  // Injected by withRouter; required, so a missing router surfaces as a propType
  // warning rather than a silent full-page reload.
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export default withRouter(ResultDeliveryPanel);
export { outboundHref };
